import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormControl, Grid2 as Grid } from '@mui/material';
import { Dayjs } from 'dayjs';
import moment from 'moment-timezone';

import { TABS as DATA_TABS } from './PolkMatchbackDataTabs';
import { AttributionCriteria } from '../../types/Criteria/AttributionCriteria';
import { Agency } from '../../types/Agency';
import { Dealer as Advertiser } from '../../types/Dealer';
import { useAttributionPageContext } from '../../hooks/useAttributionPage';
import { AGENCY_OEM, USER_GROUP_ADMIN, USER_GROUP_ADVERTISER, USER_GROUP_AGENCY } from '../../types/User';
import AccessGuard from '../../components/AccessGuard';
import AdvertiserAutocomplete from '../../components/AdvertiserAutocomplete';
import AgencyAutocomplete from '../../components/AgencyAutocomplete';
import CDMonthDatePicker from '../../components/CDMonthDatePicker';
import Column from '../../components/Column';
import Utils from '../../components/Utils';
import AgencyMarketRegionCheckboxAutoComplete from '../../components/AgencyMarketRegionCheckboxAutocomplete';
import OemCheckboxAutoComplete from '../../components/OemCheckboxAutoComplete';
import { Oem } from '../../types/Oem';
import { useUserContext } from '../../hooks/Contexts/UserContext';

interface AttributionCriteriaFormProps {
    criteria: AttributionCriteria;
    onCriteriaChange: (criteria: AttributionCriteria) => void;
}

const AttributionCriteriaForm = ({ criteria, onCriteriaChange }: AttributionCriteriaFormProps) => {
    const { userContext } = useUserContext();
    const { setIsAttributionCriteriaSetup, isAttributionCriteriaSetup, attributionDate, setAttributionDate } =
        useAttributionPageContext();

    const [isOemVisible, setIsOemVisible] = useState<boolean>(true);
    const [isDmaVisible, setIsDmaVisible] = useState<boolean>(true);
    const [isOemVisibilityInitialized, setIsOemVisibilityInitialized] = useState<boolean>(false);
    const [isDmaVisibilityInitialized, setIsDmaVisibilityInitialized] = useState<boolean>(false);

    const generateDateRange = () => {
        const dateRange: { startDate: Date; endDate: Date } = {
            startDate: moment('2024-01-01').startOf('day').toDate(),
            endDate: moment().startOf('day').toDate(),
        };

        if (!Utils.isAfterFirstSundayOfCurrentMonth()) {
            dateRange.endDate = moment(dateRange.endDate).subtract(1, 'months').toDate();
        }

        return dateRange;
    };

    const { startDate: attributionMinDate, endDate: attributionMaxDate } = generateDateRange();

    useEffect(() => {
        if (!Utils.isAfterFirstSundayOfCurrentMonth()) {
            setAttributionDate(attributionMaxDate);
        }

        localStorage.setItem('attribution.polkDataTab', DATA_TABS.SALES_ANALYSIS as any as string);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOnOemChange = useCallback(
        (oems: Oem[]): void => {
            onCriteriaChange({
                ...criteria,
                oemIds: oems.map((oem: Oem) => oem.id as number),
                oemNames: oems.map((oem: Oem) => oem.name as string),
            });
        },
        [criteria, onCriteriaChange]
    );

    const handleOnAgencyChange = useCallback(
        (agencies: Agency[]): void => {
            onCriteriaChange({
                ...criteria,
                agencyIds: agencies.map((agency: Agency) => agency.id as number),
                agencies: agencies,
            });
        },
        [criteria, onCriteriaChange]
    );

    const handleOnAdvertiserChange = useCallback(
        (advertisers: Advertiser[]): void => {
            if (!isAttributionCriteriaSetup) {
                setIsAttributionCriteriaSetup(true);
            }

            onCriteriaChange({
                ...criteria,
                advertiserIds: advertisers.map((advertiser: Advertiser) => advertiser.id as number),
                advertisers: advertisers,
            });
        },
        [criteria, onCriteriaChange, setIsAttributionCriteriaSetup, isAttributionCriteriaSetup]
    );

    const handleDmaOnChange = useCallback(
        (value: string[] | string | null): void => {
            if (!isAttributionCriteriaSetup) {
                setIsAttributionCriteriaSetup(true);
            }
            onCriteriaChange({
                ...criteria,
                agencyMarketRegions: Array.isArray(value) ? value : value ? [value] : [],
            });
        },
        [criteria, onCriteriaChange]
    );

    const handleOnAttributionDateChange = (newDate: Dayjs | null): void => {
        if (newDate) {
            setAttributionDate(newDate.toDate());
        }
    };

    const agencyCriteria = useMemo(() => {
        return {
            advertiserIds: criteria.advertiserIds,
            oemIds: criteria.oemIds,
            oemProgramIds: criteria.oemProgramIds,
            agencyMarketRegions: criteria.agencyMarketRegions,
        };
    }, [criteria]);

    const advertiserCriteria = useMemo(() => {
        return {
            agencyIds: criteria.agencyIds,
            oemIds: criteria.oemIds,
            oemProgramIds: criteria.oemProgramIds,
            agencyMarketRegions: criteria.agencyMarketRegions,
        };
    }, [criteria]);

    return (
        <Column gap={2}>
            <Grid container spacing={2}>
                <AccessGuard
                    accessGroup={USER_GROUP_ADMIN + ',' + USER_GROUP_AGENCY + ',' + USER_GROUP_ADVERTISER}
                    accessAgency={AGENCY_OEM}
                >
                    <Grid size={4} sx={{ display: isOemVisible ? 'inline-flex' : 'none' }}>
                        <FormControl fullWidth>
                            <OemCheckboxAutoComplete
                                // autoSelectSingleOption
                                multiple
                                mass
                                deferChangeUntilClose
                                size="small"
                                criteria={criteria}
                                value={criteria.oemIds}
                                onChange={handleOnOemChange}
                                onEmpty={() => {
                                    if (!userContext.isAdmin() && !isOemVisibilityInitialized) {
                                        setIsOemVisible(false);
                                        setIsOemVisibilityInitialized(true);
                                    }
                                }}
                                onNotEmpty={() => {
                                    if (!userContext.isAdmin() && !isOemVisibilityInitialized) {
                                        setIsOemVisible(true);
                                        setIsOemVisibilityInitialized(true);
                                    }
                                }}
                            />
                        </FormControl>
                    </Grid>
                </AccessGuard>

                <AccessGuard accessGroup={USER_GROUP_ADMIN}>
                    <Grid size={4}>
                        <FormControl fullWidth sx={{ '& .MuiAutocomplete-root': { width: '100%' } }}>
                            <AgencyAutocomplete
                                multiple
                                mass
                                deferChangeUntilClose
                                size="small"
                                value={criteria.agencyIds}
                                criteria={agencyCriteria}
                                onChange={handleOnAgencyChange}
                            />
                        </FormControl>
                    </Grid>
                </AccessGuard>
            </Grid>

            <Grid container spacing={2}>
                {!userContext.isAdvertiser() && (
                    <AccessGuard accessGroup={USER_GROUP_ADMIN + ',' + USER_GROUP_AGENCY}>
                        <Grid size={4} sx={{ display: isDmaVisible ? 'inline-flex' : 'none' }}>
                            <FormControl fullWidth>
                                <AgencyMarketRegionCheckboxAutoComplete
                                    multiple
                                    mass
                                    deferChangeUntilClose
                                    size="small"
                                    value={criteria.agencyMarketRegions}
                                    criteria={criteria}
                                    onChange={handleDmaOnChange}
                                    onEmpty={() => {
                                        if (!userContext.isAdmin() && !isDmaVisibilityInitialized) {
                                            setIsDmaVisible(false);
                                            setIsDmaVisibilityInitialized(true);
                                        }
                                    }}
                                    onNotEmpty={() => {
                                        if (!userContext.isAdmin() && !isDmaVisibilityInitialized) {
                                            setIsDmaVisible(true);
                                            setIsDmaVisibilityInitialized(true);
                                        }
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </AccessGuard>
                )}

                <Grid size={4}>
                    <FormControl fullWidth sx={{ '& .MuiAutocomplete-root': { width: '100%' } }}>
                        <AdvertiserAutocomplete
                            // autoSelectSingleOption
                            multiple
                            mass
                            deferChangeUntilClose
                            size="small"
                            value={criteria.advertiserIds}
                            criteria={advertiserCriteria}
                            onChange={handleOnAdvertiserChange}
                            showInactiveAdvertisersToggle={true}
                        />
                    </FormControl>
                </Grid>
                <Grid size={4}>
                    <FormControl fullWidth>
                        <CDMonthDatePicker
                            label="Month"
                            value={attributionDate}
                            minDate={attributionMinDate}
                            maxDate={attributionMaxDate}
                            onChange={handleOnAttributionDateChange}
                        />
                    </FormControl>
                </Grid>
            </Grid>
        </Column>
    );
};
export type { AttributionCriteriaFormProps };
export default AttributionCriteriaForm;
